<script setup lang="ts">
// vue
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

// third-party
import { truncate } from 'lodash'

// stores
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useTransactionStore } from '@/modules/investing/stores/transaction-store'

// composables
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useChartInsights } from '@/modules/investing/composables/use-chart-insights'
import { useColumnVisibility } from '@/modules/shared/composables/useColumnVisibility'
import { useTableExport } from '@/modules/shared/composables/useTableExport'

// local components / composables
import EntityLayout from '@/modules/investing/components/entities/entity-layout.vue'
import TransactionTypeCell from './components/transaction-type-cell.vue'
import TransactionStatusCell from './components/transaction-status-cell.vue'
import TransactionActionsCell from './components/transaction-actions-cell.vue'
import { accentColorFor } from './components/transaction-display'
import { useTransactionColumns } from './composables/use-transaction-columns'
import { useTransactionActions } from './composables/use-transaction-actions'
import { useTransactionFilters, normalize } from './composables/use-transaction-filters'
import { getDisplayType, groupKeyFor } from './utils/transaction-row'

// shared components
import {
  ActionsGroup,
  ActionItem,
  ActionsMenu,
  VAlert,
  VButton,
  VSection,
  VDataTable,
  VSearchInput,
  ActiveFilterBadges,
  FiltersDropdown,
  VSelectDropdown,
} from '@/modules/shared/components'
import CapitalSummary from '@/modules/investing/components/overview/capital-summary.vue'
import DistributionSummary from '@/modules/investing/components/overview/distribution-summary.vue'

// utils
import { investorPath } from '@/modules/investing/utils/investor'
import { initialMoney, toNumber } from '@/modules/shared/utils/money'

///////////////////////////////////////////////////////////////////////////////

const route = useRoute()
const { entity_id, entity_type, slug } = useRoute().params as { entity_id: string; entity_type: string; slug: string }
const INVESTOR_NAME_MAX_LENGTH = 35

///////////////////////////////////////////////////////////////////////////////
// Stores
///////////////////////////////////////////////////////////////////////////////

const entityStore = useEntityStore()
const transactionStore = useTransactionStore()

///////////////////////////////////////////////////////////////////////////////
// Raw Data
///////////////////////////////////////////////////////////////////////////////

const entity = computed(() => entityStore.entityExpanded)

const transactions = computed(() => transactionStore.transactions)

///////////////////////////////////////////////////////////////////////////////
// Transformed Data
///////////////////////////////////////////////////////////////////////////////

const transactionsExpanded = computed(() =>
  transactions.value.map((t) => {
    const batchId = t.transaction_batch_id ? Number(t.transaction_batch_id) : null
    const displayType = getDisplayType(t)
    const company = t.company_id ? entityStore.getCompany(Number(t.company_id)) : null
    return {
      ...t,
      company,
      amount: parseFloat(t.amount),
      transaction_batch_id: batchId,
      _group_key: groupKeyFor(t, company),
      investor_id: Number(t.investor_id),
      display_type: displayType,
      accent_color: accentColorFor(isCurrentEntity(t) ? 'distribution' : displayType),
    }
  }),
)

///////////////////////////////////////////////////////////////////////////////
// Util
///////////////////////////////////////////////////////////////////////////////

const isCurrentEntity = (t: any) => String(t.investor_type) === 'InvestorSet' && String(t.investor_id) === entity_id

///////////////////////////////////////////////////////////////////////////////
// Filters
///////////////////////////////////////////////////////////////////////////////

const filters = useTransactionFilters(transactionsExpanded, entity_id)
const filtersDropdownRef = ref<InstanceType<typeof FiltersDropdown> | null>(null)

const selectedColumnCount = computed(() => visibleColumnKeys.value.length)
const allColumnsSelected = computed(() => selectedColumnCount.value === columnOptions.value.length)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const insightsScope = computed<string[] | null>(() => {
  const selected = filters.investorFilter.value ?? []
  const total = filters.investorOptions.value.length
  return selected.length > 0 && selected.length < total ? selected : null
})

// Skeleton the summary cards while insights are (re)loading. The in-flight
// counter keeps the skeleton up across overlapping refetches (rapid filter
// changes) so it doesn't flip off early when the first response lands.
const insightsLoading = ref(false)
let insightsInFlight = 0

const loadInsights = async () => {
  insightsInFlight++
  insightsLoading.value = true
  try {
    await entityStore.fetchInsights(entity_id, { slug, shareholder_cids: insightsScope.value ?? undefined })
  } finally {
    insightsInFlight--
    if (insightsInFlight === 0) insightsLoading.value = false
  }
}

const fetch = async (shareholder_cids = null, loadSkeleton = true) => {
  if (loadSkeleton) showSkeleton()
  await Promise.all([
    (async () => {
      await entityStore.fetchEntity(entity_id, { slug, shareholder_cids })
      await loadInsights()
    })(),
    transactionStore.fetchTransactions(entity_id, { slug }),
  ])
  if (loadSkeleton) hideSkeleton()
}

watch(insightsScope, loadInsights)

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const isAdmin = computed(
  () => authStore.is_site_or_group_admin || !!entityStore.getAdmin(authStore.current_user.investor_id as string),
)

const isInvestor = computed(() => authStore.isInvestor)

///////////////////////////////////////////////////////////////////////////////
// Table
///////////////////////////////////////////////////////////////////////////////

const { columns: baseColumns } = useTransactionColumns({ entityId: entity_id })
const { visibleColumnKeys, resolvedColumns: columns, columnOptions } = useColumnVisibility(baseColumns)

const computedGroupBy = computed<string | null>(() => {
  const hasSearch = Boolean(normalize(filters.searchFilter.value))
  if (hasSearch) return null

  return filters.investorFilter.value && filters.investorFilter.value.length === 1 ? null : '_group_key'
})

const defaultSortBy: { key: string; direction: 'asc' | 'desc' }[] = [{ key: 'date', direction: 'desc' }]
const fallbackSortBy: { key: string; direction: 'asc' | 'desc' }[] = [{ key: 'investor_name', direction: 'asc' }]

const chartInsights = useChartInsights(filters.filteredItems, entity)

const totalCommitted = computed(() => toNumber(entity.value?.insights?.committed ?? initialMoney))
const fundsDeployed = computed(() => toNumber(entity.value?.insights?.initial_value ?? initialMoney))
const cashPosition = computed(() => toNumber(entity.value?.insights?.cash_position ?? initialMoney))

const investorLink = (item: any) => investorPath(`${item.investor_entity_type}:${item.investor_id}`)

// -----------------------------------------------------------------------------
// CSV Export
// -----------------------------------------------------------------------------

const { exportCsv } = useTableExport({
  columns,
  items: filters.filteredItems,
  filenamePrefix: route.params.slug,
})

const clearFilters = () => {
  filters.clearAll()
  filtersDropdownRef.value?.clearUserModified()
}

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

// Summaries skeleton on the full initial load and while an insights refetch is
// in flight; the table only skeletons on the full load.
const summariesLoading = computed(() => skeleton.value || insightsLoading.value)

const { resolveActions } = useTransactionActions({
  isAdmin,
  isInvestor,
  fetch,
})

onMounted(async () => {
  showSkeleton()
  await fetch()
  hideSkeleton()

  const queryType = route.query.type
  if (queryType) {
    filtersDropdownRef.value?.markUserModified('type')
    filters.typeFilter.value = Array.isArray(queryType) ? (queryType as string[]) : [queryType as string]
  }
})
</script>

<template>
  <EntityLayout selectedTab="transactions">
    <VSection class="sm:!mb-8">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-wrap items-center gap-3">
          <VSearchInput v-model="filters.searchFilter.value" placeholder="Search…" />
          <FiltersDropdown ref="filtersDropdownRef" :filters="filters.filtersDropdownConfig.value" />
          <VSelectDropdown v-model="visibleColumnKeys" :options="columnOptions">
            <template #default>
              <div class="flex items-center gap-2">
                <span>Columns</span>
                <span
                  v-if="!allColumnsSelected"
                  class="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600"
                >
                  {{ selectedColumnCount }}
                </span>
              </div>
            </template>
          </VSelectDropdown>
          <VButton variant="secondary" @click="exportCsv">Export CSV</VButton>
        </div>
        <ActionsMenu v-if="isAdmin">
          <template #menu_button>
            <VButton size="md" variant="v-blue">
              <div>New Transaction</div>
            </VButton>
          </template>
          <ActionsGroup>
            <ActionItem tag="RouterLink" text="Call capital" :to="{ name: 'investing.capital-call.new' }" />
            <ActionItem
              tag="RouterLink"
              text="Schedule capital call"
              :to="{ name: 'investing.capital-call.new', query: { schedule_call_capital: 'true' } }"
            />
            <ActionItem tag="RouterLink" text="Distribution" :to="{ name: 'investing.disbursement.new' }" />
            <ActionItem
              tag="RouterLink"
              text="Management Fee"
              :to="{ name: 'investing.transactions.new', query: { type: 'management_fee' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Catch-up Interest"
              :to="{ name: 'investing.transactions.new', query: { type: 'interest_allocation' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Other Fee"
              :to="{ name: 'investing.transactions.new', query: { type: 'other_fee' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Operating Income"
              :to="{ name: 'investing.transactions.new', query: { type: 'operating_income' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Operating Expense"
              :to="{ name: 'investing.transactions.new', query: { type: 'operating_expense' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Recycle Funds"
              :to="{ name: 'investing.transactions.new', query: { type: 'recycled_funds' } }"
            />
            <ActionItem
              tag="RouterLink"
              text="Syndication Cost"
              :to="{ name: 'investing.transactions.new', query: { type: 'syndication_fee' } }"
            />
          </ActionsGroup>
        </ActionsMenu>
      </div>
      <div v-if="filters.hasActiveFilters.value" class="mt-3 flex flex-wrap items-center gap-2">
        <ActiveFilterBadges
          :filters="filters.filtersDropdownConfig.value"
          @remove="(key) => filtersDropdownRef?.markUserModified(key)"
        />
        <VButton variant="invisible" size="sm" @click="clearFilters">Clear filters</VButton>
      </div>
    </VSection>

    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-5 lg:flex-row">
        <div class="min-w-0 lg:w-1/2">
          <CapitalSummary
            :calledPaid="chartInsights.calledSettledTotal.value"
            :calledPending="chartInsights.calledPendingTotal.value"
            :totalCalled="chartInsights.calledTotal.value"
            :totalCommitted="totalCommitted"
            :managementFees="chartInsights.calledManagementFees.value"
            :otherFees="chartInsights.calledOtherFees.value"
            :fundsDeployed="fundsDeployed"
            :cashPosition="cashPosition"
            :operatingIncome="chartInsights.calledOperatingIncome.value"
            :operatingExpenses="chartInsights.calledOperatingExpense.value"
            :interestPaid="chartInsights.interestAllocationPaid.value"
            :interestReceived="chartInsights.interestAllocationReceived.value"
            :currency="entity?.currency ?? 'USD'"
            :skeleton="summariesLoading"
          />
        </div>
        <div class="min-w-0 lg:w-1/2">
          <DistributionSummary
            :distributedPaid="chartInsights.distributedGross.value"
            :distributedPending="chartInsights.unpaidDistributedGross.value"
            :returnOfCapital="chartInsights.distributedReturnOfCapital.value"
            :preferredReturn="chartInsights.distributedPreferredReturn.value"
            :profit="chartInsights.distributedProfit.value"
            :carriedInterest="chartInsights.distributedCarriedInterest.value"
            :netDistributed="chartInsights.distributedNet.value"
            :otherFees="chartInsights.distributedOtherFee.value"
            :recycledFunds="chartInsights.calledRecycledFunds.value"
            :hurdle="chartInsights.totalHurdle.value"
            :currency="entity?.currency ?? 'USD'"
            :skeleton="summariesLoading"
          />
        </div>
      </div>

      <div class="min-w-0">
        <VDataTable
          :columns="columns"
          :items="filters.filteredItems.value"
          :groupBy="computedGroupBy"
          :sortBy="defaultSortBy"
          :fallbackSortBy="fallbackSortBy"
          :empty-title="filters.tableEmptyTitle.value"
          :empty-message="filters.tableEmptyMessage.value"
          :loading="skeleton"
          :canExpand="
            (items) =>
              !(items.length === 1 && items[0].type === 'portfolio_distribution' && !items[0].transaction_batch_id)
          "
        >
          <template #type="{ item, value, group }">
            <TransactionTypeCell :value="value" :item="item" :group="group" />
          </template>
          <template #company="{ value, mode }">
            <template v-if="mode === 'aggregate'"> {{ value?.name }}</template>
          </template>
          <template #investor_name="{ item, formatted, value, mode }">
            <template v-if="mode === 'aggregate' && value > 0">
              {{ value }} Investor{{ value === 1 ? '' : 's' }}
            </template>
            <template v-else-if="mode !== 'aggregate'">
              <RouterLink
                class="hyperlink"
                :to="investorLink(item)"
                :title="formatted?.length > INVESTOR_NAME_MAX_LENGTH ? formatted : undefined"
              >
                {{ truncate(formatted, { length: INVESTOR_NAME_MAX_LENGTH + 3 }) }}
              </RouterLink>
            </template>
          </template>
          <template #status="{ value }">
            <TransactionStatusCell
              :value="value"
              :isAdmin="isAdmin"
              :entityId="entity_id"
              :entityType="entity_type"
              :refetch="async () => await fetch()"
            />
          </template>
          <template #actions="{ item, group, mode }">
            <TransactionActionsCell
              :actions="resolveActions(item, mode)"
              :item="item"
              :group="group"
              :isInvestor="isInvestor && !isAdmin"
            />
          </template>
        </VDataTable>
      </div>
    </div>
  </EntityLayout>
</template>
